import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import prefetchHelper from "./prefetch";
import emitter from "./emitter";
import { setMatchPaths, findPath, findMatchPath as _findMatchPath } from "./find-path";

/**
 * Available resource loading statuses
 */
export var PageResourceStatus = {
  /**
   * At least one of critical resources failed to load
   */
  Error: "error",
  /**
   * Resources loaded successfully
   */
  Success: "success"
};
var preferDefault = function preferDefault(m) {
  return m && m.default || m;
};
var stripSurroundingSlashes = function stripSurroundingSlashes(s) {
  s = s[0] === "/" ? s.slice(1) : s;
  s = s.endsWith("/") ? s.slice(0, -1) : s;
  return s;
};
var createPageDataUrl = function createPageDataUrl(path) {
  var fixedPath = path === "/" ? "index" : stripSurroundingSlashes(path);
  return __PATH_PREFIX__ + "/page-data/" + fixedPath + "/page-data.json";
};
function doFetch(url, method) {
  if (method === void 0) {
    method = "GET";
  }
  return new Promise(function (resolve, reject) {
    var req = new XMLHttpRequest();
    req.open(method, url, true);
    req.onreadystatechange = function () {
      if (req.readyState == 4) {
        resolve(req);
      }
    };
    req.send(null);
  });
}
var doesConnectionSupportPrefetch = function doesConnectionSupportPrefetch() {
  if ("connection" in navigator && typeof navigator.connection !== "undefined") {
    if ((navigator.connection.effectiveType || "").includes("2g")) {
      return false;
    }
    if (navigator.connection.saveData) {
      return false;
    }
  }
  return true;
};
var toPageResources = function toPageResources(pageData, component) {
  if (component === void 0) {
    component = null;
  }
  var page = {
    componentChunkName: pageData.componentChunkName,
    path: pageData.path,
    webpackCompilationHash: pageData.webpackCompilationHash,
    matchPath: pageData.matchPath,
    staticQueryHashes: pageData.staticQueryHashes
  };
  return {
    component: component,
    json: pageData.result,
    page: page
  };
};
export var BaseLoader = /*#__PURE__*/function () {
  function BaseLoader(loadComponent, matchPaths) {
    this.inFlightNetworkRequests = new Map();
    // Map of pagePath -> Page. Where Page is an object with: {
    //   status: PageResourceStatus.Success || PageResourceStatus.Error,
    //   payload: PageResources, // undefined if PageResourceStatus.Error
    // }
    // PageResources is {
    //   component,
    //   json: pageData.result,
    //   page: {
    //     componentChunkName,
    //     path,
    //     webpackCompilationHash,
    //     staticQueryHashes
    //   },
    //   staticQueryResults
    // }
    this.pageDb = new Map();
    this.inFlightDb = new Map();
    this.staticQueryDb = {};
    this.pageDataDb = new Map();
    this.prefetchTriggered = new Set();
    this.prefetchCompleted = new Set();
    this.loadComponent = loadComponent;
    setMatchPaths(matchPaths);
  }
  var _proto = BaseLoader.prototype;
  _proto.memoizedGet = function memoizedGet(url) {
    var _this = this;
    var inFlightPromise = this.inFlightNetworkRequests.get(url);
    if (!inFlightPromise) {
      inFlightPromise = doFetch(url, "GET");
      this.inFlightNetworkRequests.set(url, inFlightPromise);
    }

    // Prefer duplication with then + catch over .finally to prevent problems in ie11 + firefox
    return inFlightPromise.then(function (response) {
      _this.inFlightNetworkRequests.delete(url);
      return response;
    }).catch(function (err) {
      _this.inFlightNetworkRequests.delete(url);
      throw err;
    });
  };
  _proto.setApiRunner = function setApiRunner(apiRunner) {
    this.apiRunner = apiRunner;
    this.prefetchDisabled = apiRunner("disableCorePrefetching").some(function (a) {
      return a;
    });
  };
  _proto.fetchPageDataJson = function fetchPageDataJson(loadObj) {
    var _this2 = this;
    var pagePath = loadObj.pagePath,
      _loadObj$retries = loadObj.retries,
      retries = _loadObj$retries === void 0 ? 0 : _loadObj$retries;
    var url = createPageDataUrl(pagePath);
    return this.memoizedGet(url).then(function (req) {
      var status = req.status,
        responseText = req.responseText;

      // Handle 200
      if (status === 200) {
        try {
          var jsonPayload = JSON.parse(responseText);
          if (jsonPayload.path === undefined) {
            throw new Error("not a valid pageData response");
          }
          return Object.assign(loadObj, {
            status: PageResourceStatus.Success,
            payload: jsonPayload
          });
        } catch (err) {
          // continue regardless of error
        }
      }

      // Handle 404
      if (status === 404 || status === 200) {
        // If the request was for a 404 page and it doesn't exist, we're done
        if (pagePath === "/404.html") {
          return Object.assign(loadObj, {
            status: PageResourceStatus.Error
          });
        }

        // Need some code here to cache the 404 request. In case
        // multiple loadPageDataJsons result in 404s
        return _this2.fetchPageDataJson(Object.assign(loadObj, {
          pagePath: "/404.html",
          notFound: true
        }));
      }

      // handle 500 response (Unrecoverable)
      if (status === 500) {
        return Object.assign(loadObj, {
          status: PageResourceStatus.Error
        });
      }

      // Handle everything else, including status === 0, and 503s. Should retry
      if (retries < 3) {
        return _this2.fetchPageDataJson(Object.assign(loadObj, {
          retries: retries + 1
        }));
      }

      // Retried 3 times already, result is an error.
      return Object.assign(loadObj, {
        status: PageResourceStatus.Error
      });
    });
  };
  _proto.loadPageDataJson = function loadPageDataJson(rawPath) {
    var _this3 = this;
    var pagePath = findPath(rawPath);
    if (this.pageDataDb.has(pagePath)) {
      var pageData = this.pageDataDb.get(pagePath);
      if (process.env.BUILD_STAGE !== "develop" || !pageData.stale) {
        return Promise.resolve(pageData);
      }
    }
    return this.fetchPageDataJson({
      pagePath: pagePath
    }).then(function (pageData) {
      _this3.pageDataDb.set(pagePath, pageData);
      return pageData;
    });
  };
  _proto.findMatchPath = function findMatchPath(rawPath) {
    return _findMatchPath(rawPath);
  }

  // TODO check all uses of this and whether they use undefined for page resources not exist
  ;

  _proto.loadPage = function loadPage(rawPath) {
    var _this4 = this;
    var pagePath = findPath(rawPath);
    if (this.pageDb.has(pagePath)) {
      var page = this.pageDb.get(pagePath);
      if (process.env.BUILD_STAGE !== "develop" || !page.payload.stale) {
        return Promise.resolve(page.payload);
      }
    }
    if (this.inFlightDb.has(pagePath)) {
      return this.inFlightDb.get(pagePath);
    }
    var inFlightPromise = Promise.all([this.loadAppData(), this.loadPageDataJson(pagePath)]).then(function (allData) {
      var result = allData[1];
      if (result.status === PageResourceStatus.Error) {
        return {
          status: PageResourceStatus.Error
        };
      }
      var pageData = result.payload;
      var _pageData = pageData,
        componentChunkName = _pageData.componentChunkName,
        _pageData$staticQuery = _pageData.staticQueryHashes,
        staticQueryHashes = _pageData$staticQuery === void 0 ? [] : _pageData$staticQuery;
      var finalResult = {};
      var componentChunkPromise = _this4.loadComponent(componentChunkName).then(function (component) {
        finalResult.createdAt = new Date();
        var pageResources;
        if (!component) {
          finalResult.status = PageResourceStatus.Error;
        } else {
          finalResult.status = PageResourceStatus.Success;
          if (result.notFound === true) {
            finalResult.notFound = true;
          }
          pageData = Object.assign(pageData, {
            webpackCompilationHash: allData[0] ? allData[0].webpackCompilationHash : ""
          });
          pageResources = toPageResources(pageData, component);
        }
        // undefined if final result is an error
        return pageResources;
      });
      var staticQueryBatchPromise = Promise.all(staticQueryHashes.map(function (staticQueryHash) {
        // Check for cache in case this static query result has already been loaded
        if (_this4.staticQueryDb[staticQueryHash]) {
          var jsonPayload = _this4.staticQueryDb[staticQueryHash];
          return {
            staticQueryHash: staticQueryHash,
            jsonPayload: jsonPayload
          };
        }
        return _this4.memoizedGet(__PATH_PREFIX__ + "/page-data/sq/d/" + staticQueryHash + ".json").then(function (req) {
          var jsonPayload = JSON.parse(req.responseText);
          return {
            staticQueryHash: staticQueryHash,
            jsonPayload: jsonPayload
          };
        });
      })).then(function (staticQueryResults) {
        var staticQueryResultsMap = {};
        staticQueryResults.forEach(function (_ref) {
          var staticQueryHash = _ref.staticQueryHash,
            jsonPayload = _ref.jsonPayload;
          staticQueryResultsMap[staticQueryHash] = jsonPayload;
          _this4.staticQueryDb[staticQueryHash] = jsonPayload;
        });
        return staticQueryResultsMap;
      });
      return Promise.all([componentChunkPromise, staticQueryBatchPromise]).then(function (_ref2) {
        var pageResources = _ref2[0],
          staticQueryResults = _ref2[1];
        var payload;
        if (pageResources) {
          payload = _objectSpread(_objectSpread({}, pageResources), {}, {
            staticQueryResults: staticQueryResults
          });
          finalResult.payload = payload;
          emitter.emit("onPostLoadPageResources", {
            page: payload,
            pageResources: payload
          });
        }
        _this4.pageDb.set(pagePath, finalResult);
        return payload;
      });
    });
    inFlightPromise.then(function (response) {
      _this4.inFlightDb.delete(pagePath);
    }).catch(function (error) {
      _this4.inFlightDb.delete(pagePath);
      throw error;
    });
    this.inFlightDb.set(pagePath, inFlightPromise);
    return inFlightPromise;
  }

  // returns undefined if loading page ran into errors
  ;

  _proto.loadPageSync = function loadPageSync(rawPath) {
    var pagePath = findPath(rawPath);
    if (this.pageDb.has(pagePath)) {
      var pageData = this.pageDb.get(pagePath).payload;
      return pageData;
    }
    return undefined;
  };
  _proto.shouldPrefetch = function shouldPrefetch(pagePath) {
    // Skip prefetching if we know user is on slow or constrained connection
    if (!doesConnectionSupportPrefetch()) {
      return false;
    }

    // Check if the page exists.
    if (this.pageDb.has(pagePath)) {
      return false;
    }
    return true;
  };
  _proto.prefetch = function prefetch(pagePath) {
    var _this5 = this;
    if (!this.shouldPrefetch(pagePath)) {
      return false;
    }

    // Tell plugins with custom prefetching logic that they should start
    // prefetching this path.
    if (!this.prefetchTriggered.has(pagePath)) {
      this.apiRunner("onPrefetchPathname", {
        pathname: pagePath
      });
      this.prefetchTriggered.add(pagePath);
    }

    // If a plugin has disabled core prefetching, stop now.
    if (this.prefetchDisabled) {
      return false;
    }
    var realPath = findPath(pagePath);
    // Todo make doPrefetch logic cacheable
    // eslint-disable-next-line consistent-return
    this.doPrefetch(realPath).then(function () {
      if (!_this5.prefetchCompleted.has(pagePath)) {
        _this5.apiRunner("onPostPrefetchPathname", {
          pathname: pagePath
        });
        _this5.prefetchCompleted.add(pagePath);
      }
    });
    return true;
  };
  _proto.doPrefetch = function doPrefetch(pagePath) {
    var _this6 = this;
    var pageDataUrl = createPageDataUrl(pagePath);
    return prefetchHelper(pageDataUrl, {
      crossOrigin: "anonymous",
      as: "fetch"
    }).then(function () {
      return (
        // This was just prefetched, so will return a response from
        // the cache instead of making another request to the server
        _this6.loadPageDataJson(pagePath)
      );
    });
  };
  _proto.hovering = function hovering(rawPath) {
    this.loadPage(rawPath);
  };
  _proto.getResourceURLsForPathname = function getResourceURLsForPathname(rawPath) {
    var pagePath = findPath(rawPath);
    var page = this.pageDataDb.get(pagePath);
    if (page) {
      var pageResources = toPageResources(page.payload);
      return [].concat(_toConsumableArray(createComponentUrls(pageResources.page.componentChunkName)), [createPageDataUrl(pagePath)]);
    } else {
      return null;
    }
  };
  _proto.isPageNotFound = function isPageNotFound(rawPath) {
    var pagePath = findPath(rawPath);
    var page = this.pageDb.get(pagePath);
    return !page || page.notFound;
  };
  _proto.loadAppData = function loadAppData(retries) {
    var _this7 = this;
    if (retries === void 0) {
      retries = 0;
    }
    return this.memoizedGet(__PATH_PREFIX__ + "/page-data/app-data.json").then(function (req) {
      var status = req.status,
        responseText = req.responseText;
      var appData;
      if (status !== 200 && retries < 3) {
        // Retry 3 times incase of non-200 responses
        return _this7.loadAppData(retries + 1);
      }

      // Handle 200
      if (status === 200) {
        try {
          var jsonPayload = JSON.parse(responseText);
          if (jsonPayload.webpackCompilationHash === undefined) {
            throw new Error("not a valid app-data response");
          }
          appData = jsonPayload;
        } catch (err) {
          // continue regardless of error
        }
      }
      return appData;
    });
  };
  return BaseLoader;
}();
var createComponentUrls = function createComponentUrls(componentChunkName) {
  return (window.___chunkMapping[componentChunkName] || []).map(function (chunk) {
    return __PATH_PREFIX__ + chunk;
  });
};
export var ProdLoader = /*#__PURE__*/function (_BaseLoader) {
  _inheritsLoose(ProdLoader, _BaseLoader);
  function ProdLoader(asyncRequires, matchPaths) {
    var loadComponent = function loadComponent(chunkName) {
      return asyncRequires.components[chunkName] ? asyncRequires.components[chunkName]().then(preferDefault)
      // loader will handle the case when component is null
      .catch(function () {
        return null;
      }) : Promise.resolve();
    };
    return _BaseLoader.call(this, loadComponent, matchPaths) || this;
  }
  var _proto2 = ProdLoader.prototype;
  _proto2.doPrefetch = function doPrefetch(pagePath) {
    return _BaseLoader.prototype.doPrefetch.call(this, pagePath).then(function (result) {
      if (result.status !== PageResourceStatus.Success) {
        return Promise.resolve();
      }
      var pageData = result.payload;
      var chunkName = pageData.componentChunkName;
      var componentUrls = createComponentUrls(chunkName);
      return Promise.all(componentUrls.map(prefetchHelper)).then(function () {
        return pageData;
      });
    });
  };
  _proto2.loadPageDataJson = function loadPageDataJson(rawPath) {
    return _BaseLoader.prototype.loadPageDataJson.call(this, rawPath).then(function (data) {
      if (data.notFound) {
        // check if html file exist using HEAD request:
        // if it does we should navigate to it instead of showing 404
        return doFetch(rawPath, "HEAD").then(function (req) {
          if (req.status === 200) {
            // page (.html file) actually exist (or we asked for 404 )
            // returning page resources status as errored to trigger
            // regular browser navigation to given page
            return {
              status: PageResourceStatus.Error
            };
          }

          // if HEAD request wasn't 200, return notFound result
          // and show 404 page
          return data;
        });
      }
      return data;
    });
  };
  return ProdLoader;
}(BaseLoader);
var instance;
export var setLoader = function setLoader(_loader) {
  instance = _loader;
};
export var publicLoader = {
  // Deprecated methods. As far as we're aware, these are only used by
  // core gatsby and the offline plugin, however there's a very small
  // chance they're called by others.
  getResourcesForPathname: function getResourcesForPathname(rawPath) {
    console.warn("Warning: getResourcesForPathname is deprecated. Use loadPage instead");
    return instance.i.loadPage(rawPath);
  },
  getResourcesForPathnameSync: function getResourcesForPathnameSync(rawPath) {
    console.warn("Warning: getResourcesForPathnameSync is deprecated. Use loadPageSync instead");
    return instance.i.loadPageSync(rawPath);
  },
  enqueue: function enqueue(rawPath) {
    return instance.prefetch(rawPath);
  },
  // Real methods
  getResourceURLsForPathname: function getResourceURLsForPathname(rawPath) {
    return instance.getResourceURLsForPathname(rawPath);
  },
  loadPage: function loadPage(rawPath) {
    return instance.loadPage(rawPath);
  },
  loadPageSync: function loadPageSync(rawPath) {
    return instance.loadPageSync(rawPath);
  },
  prefetch: function prefetch(rawPath) {
    return instance.prefetch(rawPath);
  },
  isPageNotFound: function isPageNotFound(rawPath) {
    return instance.isPageNotFound(rawPath);
  },
  hovering: function hovering(rawPath) {
    return instance.hovering(rawPath);
  },
  loadAppData: function loadAppData() {
    return instance.loadAppData();
  }
};
export default publicLoader;
export function getStaticQueryResults() {
  if (instance) {
    return instance.staticQueryDb;
  } else {
    return {};
  }
}